/**
 * Cart actions notifications
 *
 * Shows notifications cloned from prerendered Liquid templates
 * Loaded by sections/cart-drawer.liquid and sections/main-cart.liquid.
 *
 * Consumers: assets/cart.js, assets/extra-services.js, assets/product-form.js —
 *
 * Wrapped in an IIFE with a window guard because both cart sections load this
 * file and could theoretically coexist on one page.
 */
(function () {
  if (typeof window.showCartNotification === 'function') return;

  /**
   * Single place to enable/disable each cart notification VISUALLY.
   *
   * `false` hides the visual notification but the message is still announced
   * to assistive technology via the theme's visually-hidden live regions
   * (#cart-live-region-text / #CartDrawer-LiveRegionText), so screen reader
   * users are always informed of cart changes (WCAG 4.1.3 Status Messages).
   */
  window.cartNotificationSettings = {
    'item-added': true,
    'item-quantity-changed': false,
    'item-removed': false,
    'service-added': false,
    'service-removed': false,
    message: true,
  };

  /**
   * Announce a cart status message to the closest visually-hidden live region.
   * Used when a notification is visually disabled — visible notifications
   * announce themselves via their own role="status".
   * @param {string} text - Plain-text message.
   * @param {HTMLElement|null} container - The would-be notification container (used to pick page vs drawer region).
   */
  function announceCartNotification(text, container) {
    const inDrawer = !!container?.closest('cart-drawer');
    const region =
      (inDrawer && document.getElementById('CartDrawer-LiveRegionText')) ||
      document.getElementById('cart-live-region-text') ||
      document.getElementById('CartDrawer-LiveRegionText');
    if (!region || !text) return;

    // Clear first so repeating the same message is re-announced.
    // setTimeout (not requestAnimationFrame): rAF is paused in background
    // tabs, which would silently drop the announcement.
    region.textContent = '';
    setTimeout(() => {
      region.textContent = text;
    }, 50);
  }

  /**
   * Show a cart notification cloned from a prerendered Liquid template.
   *
   * Templates carry the full markup: classes, icon, translated text with literal
   * `{{ title }}` / `{{ value }}` / `{{ message }}` placeholders. This helper
   * clones the template, substitutes placeholders (HTML-escaped) and prepends
   * the result to the target container.
   *
   * Visual display is gated by window.cartNotificationSettings[name]; when
   * disabled, the message is still announced to a visually-hidden live region
   * for assistive technology.
   *
   * @param {string} name - Template name, e.g. 'item-added' (matches [data-cart-notification]).
   * @param {object} [replacements] - Placeholder values, e.g. { title: 'Product name', value: 2 }.
   * @param {object} [options]
   * @param {HTMLElement} [options.container] - Target container (default: #cart-actions).
   * @param {string} [options.type] - Override severity: 'success' | 'warning' | 'alert'.
   * @param {number} [options.duration] - Auto-remove after ms; 0 keeps it (default: 4000).
   * @returns {HTMLElement|null} The inserted notification element, or null (not found or visually disabled).
   */
  window.showCartNotification = function showCartNotification(name, replacements = {}, options = {}) {
    const { container = document.getElementById('cart-actions'), type = null, duration = 4000 } = options;

    const template = document.querySelector(`template[data-cart-notification="${name}"]`);
    const source = template?.content?.firstElementChild;
    if (!container || !source) return null;

    const notification = source.cloneNode(true);

    if (type) {
      notification.classList.remove('notification--success', 'notification--warning', 'notification--alert');
      notification.classList.add(`notification--${type}`);
    }

    let html = notification.innerHTML;
    Object.entries(replacements).forEach(([key, value]) => {
      const escaped = document.createElement('span');
      escaped.textContent = String(value);
      html = html.replaceAll(`{{ ${key} }}`, escaped.innerHTML);
    });
    notification.innerHTML = html;

    // Visually disabled → announce to assistive technology only
    if (window.cartNotificationSettings?.[name] === false) {
      announceCartNotification(notification.textContent.trim(), container);
      return null;
    }

    container.prepend(notification);

    if (duration > 0) {
      setTimeout(() => notification.remove(), duration);
    }

    return notification;
  };
})();
